import React, { useMemo, useState } from 'react';
import { CheckCircle, Circle, Trash2, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

const normalizeStatus = (status: string) => status === 'open' ? 'pending' : status;

const TasksListView = React.memo(({ tasks, currentUser, onUpdateTask, onDeleteTask, onShowTaskModal, allUsers }: any) => {
    const [filterStatus, setFilterStatus] = useState<'pending' | 'done'>('pending');
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const myTasks = useMemo(() => {
        let filtered = tasks.filter((t: any) => 
            currentUser.role === 'admin' || 
            t.assignedTo === currentUser.name || 
            t.assignedTo === 'Alle'
        );

        filtered = filtered.filter((t: any) => normalizeStatus(t.status) === filterStatus);

        return filtered.sort((a: any, b: any) => {
            const aStatus = normalizeStatus(a.status);
            const bStatus = normalizeStatus(b.status);
            if (aStatus !== bStatus) return aStatus === 'pending' ? -1 : 1;
            return 0;
        });
    }, [tasks, currentUser, filterStatus]);

    const stats = {
        total: tasks.filter((t: any) => 
            currentUser.role === 'admin' || 
            t.assignedTo === currentUser.name || 
            t.assignedTo === 'Alle'
        ).length,
        pending: tasks.filter((t: any) => 
            (currentUser.role === 'admin' || 
            t.assignedTo === currentUser.name || 
            t.assignedTo === 'Alle') &&
            normalizeStatus(t.status) === 'pending'
        ).length,
        done: tasks.filter((t: any) => 
            (currentUser.role === 'admin' || 
            t.assignedTo === currentUser.name || 
            t.assignedTo === 'Alle') &&
            normalizeStatus(t.status) === 'done'
        ).length
    };

    const formatDate = (dateStr: any) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString('de-DE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const addWorkingDays = (startDate: Date, days: number) => {
        let current = new Date(startDate);
        let added = 0;
        while (added < days) {
            current.setDate(current.getDate() + 1);
            const dayOfWeek = current.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                added++;
            }
        }
        return current;
    };

    const isOverdueByWorkdays = (createdAt: string, status: string) => {
        if (!createdAt || status === 'done') return false;
        const created = new Date(createdAt);
        const deadline = addWorkingDays(created, 3);
        return new Date() > deadline;
    };

    return (
        <PageLayout>
            <PageHeader
                title="Aufgaben"
                subtitle={
                    <span className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${isDark ? 'bg-cyan-500 text-white' : 'bg-cyan-600 text-white'}`}>{stats.pending}</span>
                        offene von {stats.total} Aufgaben
                    </span>
                }
            />

            <PageCard className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex gap-3 flex-wrap">
                    <button
                        onClick={() => setFilterStatus('pending')}
                        className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all transform hover:scale-105 border ${
                            filterStatus === 'pending'
                                ? isDark ? 'bg-cyan-500/50 border-cyan-400/80 text-white shadow-lg shadow-cyan-500/20' : 'bg-cyan-600 border-cyan-600 text-white shadow-lg'
                                : isDark ? 'bg-white/10 border-white/20 text-white/60 hover:text-white/80' : 'bg-slate-100 border-slate-200 text-slate-600 hover:text-slate-900'
                        }`}
                        title="Zeige offene Aufgaben"
                    >
                        Offen ({stats.pending})
                    </button>
                    <button
                        onClick={() => setFilterStatus('done')}
                        className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all transform hover:scale-105 border ${
                            filterStatus === 'done'
                                ? isDark ? 'bg-green-500/50 border-green-400/80 text-white shadow-lg shadow-green-500/20' : 'bg-green-600 border-green-600 text-white shadow-lg'
                                : isDark ? 'bg-white/10 border-white/20 text-white/60 hover:text-white/80' : 'bg-slate-100 border-slate-200 text-slate-600 hover:text-slate-900'
                        }`}
                        title="Zeige erledigte Aufgaben"
                    >
                        Erledigt ({stats.done})
                    </button>
                    </div>
                    <button
                        onClick={() => onShowTaskModal(true)}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border font-bold text-sm shadow-lg hover:shadow-xl hover:scale-105 transition-all ${isDark ? 'bg-cyan-500/50 border-cyan-400/80 text-white shadow-cyan-500/20' : 'bg-cyan-600 border-cyan-600 text-white'}`}
                        title="Neue Aufgabe erstellen"
                    >
                        <Plus size={18} strokeWidth={3}/> Neue Aufgabe
                    </button>
                </div>
            </PageCard>

            {myTasks.length === 0 ? (
                <PageCard>
                <div className={`text-center py-16 ${pageStyles.empty(isDark)}`}>
                    <CheckCircle size={64} className={`mx-auto mb-4 ${isDark ? 'text-white/30' : 'text-slate-300'}`} strokeWidth={1.5}/>
                    <p className={`font-semibold text-lg mb-2 ${isDark ? 'text-white/80' : 'text-slate-800'}`}>
                        {filterStatus === 'done' ? 'Noch keine erledigten Aufgaben' : 'Keine offenen Aufgaben'}
                    </p>
                    <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                        {filterStatus === 'done' ? 'Erledigte Aufgaben erscheinen hier' : 'Erstelle deine erste Aufgabe'}
                    </p>
                    <div className="mt-4 flex justify-center">
                        <button
                            onClick={() => onShowTaskModal(true)}
                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl border text-sm font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all ${isDark ? 'bg-cyan-500/50 border-cyan-400/80 text-white shadow-cyan-500/20' : 'bg-cyan-600 border-cyan-600 text-white'}`}
                        >
                            <Plus size={18} strokeWidth={3}/> Neue Aufgabe erstellen
                        </button>
                    </div>
                </div>
                </PageCard>
            ) : (
                <PageCard className="p-4">
                <div className="space-y-3">
                    {myTasks.map((task: any) => {
                        const isTaskDone = task.status === 'done';
                        const isOverdue = isOverdueByWorkdays(task.createdAt, task.status);
                        
                        return (
                            <div
                                key={task.id}
                                className={`flex items-start gap-4 p-4 rounded-2xl border transition-all hover:shadow-md ${
                                    isOverdue 
                                        ? isDark ? 'glass-card bg-red-500/20 border-red-400/30' : 'bg-red-50 border-red-300'
                                        : isTaskDone
                                        ? isDark ? 'glass-card bg-green-500/20 border-green-400/30' : 'bg-green-50 border-green-300'
                                        : isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200'
                                }`}
                            >
                                <button
                                    onClick={() => onUpdateTask(task.id, isTaskDone ? 'pending' : 'done')}
                                    className={`flex-shrink-0 w-6 h-6 rounded-full transition-colors ${
                                        isTaskDone 
                                            ? 'bg-green-500 flex items-center justify-center' 
                                            : isDark ? 'border-2 border-white/30 hover:border-cyan-400' : 'border-2 border-slate-300 hover:border-cyan-500'
                                    }`}
                                    title={isTaskDone ? 'Als offen markieren' : 'Als erledigt markieren'}
                                >
                                    {isTaskDone && <CheckCircle className="text-white" size={16}/>}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div>
                                        <h4 className={`text-base font-semibold mb-1 ${
                                            isTaskDone ? isDark ? 'line-through text-white/60' : 'line-through text-slate-500' : isDark ? 'text-white' : 'text-slate-900'
                                        }`}>
                                            {task.title}
                                        </h4>
                                        {task.description && (
                                            <p className={`text-sm mb-2 ${
                                                isTaskDone ? isDark ? 'text-white/50' : 'text-slate-500' : isDark ? 'text-white/70' : 'text-slate-700'
                                            }`}>
                                                {task.description}
                                            </p>
                                        )}
                                        <div className={`flex gap-3 mt-2 flex-wrap text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                                            {task.assignedTo && (
                                                <span className={`px-2 py-1 rounded-lg ${isDark ? 'bg-white/10 text-white/70' : 'bg-slate-100 text-slate-700'}`}>
                                                    {task.assignedTo}
                                                </span>
                                            )}
                                            <span className={`px-2 py-1 rounded-lg border ${isDark ? 'bg-white/5 text-white/70 border-white/10' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                                                Erstellt: {formatDate(task.createdAt) || '–'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    {currentUser.role === 'admin' && (
                                        <button
                                            onClick={() => onDeleteTask(task.id)}
                                            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-red-300 hover:bg-red-500/20' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'}`}
                                            title="Aufgabe löschen"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                </PageCard>
            )}
        </PageLayout>
    );
});

TasksListView.displayName = 'TasksListView';
export default TasksListView;
